// Polyfill for requestFullScreen()

/* eslint-disable */
import videojs from 'video.js';
import screenfull from 'screenfull';
import { Howler } from 'howler';
import Statistics from './Statistics';
import VideoDOM from './VideoDOM';
import AudioHandler from './AudioHandler';
import iltPoster from './assets/images/ilt-poster.png';
import type { VideoPlayerConfig, VideoSlicePlayed } from './types';
import type {
  ILTPlayer,
  IOsDevice,
  LockableScreenOrientation,
  ResolvedVideoPlayerConfig,
  VhsApi,
} from './internal-types';

// This is a patch to fix an issue in the Android app.
//
// Capacitor's CapacitorHttp plugin patches
// XMLHttpRequest and proxies absolute URLs through `<appOrigin>/_capacitor_http_interceptor_?u=<realUrl>`.
// The proxy URL also becomes the XHR's `responseURL`, and video.js' HLS engine (VHS)
// resolves relative playlist/segment URIs against `responseURL`. So a master playlist's
// relative rendition URI (e.g. `playlist_360.m3u8`) gets resolved against the app origin
// instead of the CDN and fetches the wrong resource.
//
// Fix: on every VHS response, rewrite the proxied `responseURL` back to the real URL so
// VHS resolves relative URIs correctly. The request itself still goes through CapacitorHttp's
// proxy, so its CORS bypass is preserved. No-op on web / when CapacitorHttp is not proxying.
type VideojsWithVhs = typeof videojs & { Vhs?: VhsApi };
if ((videojs as VideojsWithVhs).Vhs?.xhr?.onResponse) {
  (videojs as VideojsWithVhs).Vhs!.xhr!.onResponse!((request) => {
    const proxied = request?.responseURL;
    if (typeof proxied === 'string' && proxied.indexOf('_capacitor_http_interceptor_') !== -1) {
      try {
        const real = new URL(proxied).searchParams.get('u');
        if (real) {
          // For GET requests CapacitorHttp uses the native (read-only) responseURL, so a
          // plain assignment is silently ignored — define an own property to shadow it.
          Object.defineProperty(request, 'responseURL', {
            value: real,
            writable: true,
            configurable: true,
          });
        }
      } catch {
        // leave responseURL untouched if it can't be parsed/rewritten
      }
    }
  });
}

/** The Fullscreen API names are looked up dynamically, so `document` is indexed as a plain record. */
type IndexedDocument = Document & Record<string, any>;

/**
 * Maps a vendor-prefixed Fullscreen API onto the standard names, for browsers that only ship
 * the prefixed one. A no-op wherever `document.fullscreenEnabled` already exists.
 */
!(function (a: IndexedDocument) {
  'use strict';

  type FullscreenApi = {
    enabled: string;
    element: string;
    request: string;
    exit: string;
    events: { change: string; error: string };
  };

  function b(b: string, c: EventTarget) {
    const d = a.createEvent('Event');
    d.initEvent(b, !0, !1), c.dispatchEvent(d);
  }

  function c(c: Event) {
    c.stopPropagation(),
      c.stopImmediatePropagation(),
      (a[j.enabled] = a[f!.enabled]),
      (a[j.element] = a[f!.element]),
      b(j.events.change, c.target!);
  }

  function d(a: Event) {
    b(j.events.error, a.target!);
  }

  function e(b: string) {
    return function (c: (value?: unknown) => void, d: (reason?: unknown) => void) {
      function e() {
        c(), a.removeEventListener(f!.events.change, e, !1);
      }

      function g() {
        d(new TypeError()), a.removeEventListener(f!.events.error, g, !1);
      }

      return b !== j.exit || a[f!.element]
        ? (a.addEventListener(f!.events.change, e, !1), void a.addEventListener(f!.events.error, g, !1))
        : void setTimeout(function () {
            d(new TypeError());
          }, 1);
    };
  }

  let f: FullscreenApi | undefined;
  let g: keyof typeof i;
  const h = !0;
  const i: Record<string, FullscreenApi> = {
    w3: {
      enabled: 'fullscreenEnabled',
      element: 'fullscreenElement',
      request: 'requestFullscreen',
      exit: 'exitFullscreen',
      events: { change: 'fullscreenchange', error: 'fullscreenerror' },
    },
    webkit: {
      enabled: 'webkitFullscreenEnabled',
      element: 'webkitCurrentFullScreenElement',
      request: 'webkitRequestFullscreen',
      exit: 'webkitExitFullscreen',
      events: { change: 'webkitfullscreenchange', error: 'webkitfullscreenerror' },
    },
    moz: {
      enabled: 'mozFullScreenEnabled',
      element: 'mozFullScreenElement',
      request: 'mozRequestFullScreen',
      exit: 'mozCancelFullScreen',
      events: { change: 'mozfullscreenchange', error: 'mozfullscreenerror' },
    },
    ms: {
      enabled: 'msFullscreenEnabled',
      element: 'msFullscreenElement',
      request: 'msRequestFullscreen',
      exit: 'msExitFullscreen',
      events: { change: 'MSFullscreenChange', error: 'MSFullscreenError' },
    },
  };
  const j = i.w3;
  for (g in i)
    if (i[g].enabled in a) {
      f = i[g];
      break;
    }
  return (
    !h ||
      j.enabled in a ||
      !f ||
      (a.addEventListener(f.events.change, c, !1),
      a.addEventListener(f.events.error, d, !1),
      (a[j.enabled] = a[f.enabled]),
      (a[j.element] = a[f.element]),
      (a[j.exit] = function () {
        const b = a[f!.exit]();
        return !b && window.Promise ? new Promise(e(j.exit)) : b;
      }),
      ((Element.prototype as unknown as Record<string, any>)[j.request] = function (this: Element) {
        const a = (this as unknown as Record<string, any>)[f!.request].apply(this, arguments);
        return !a && window.Promise ? new Promise(e(j.request)) : a;
      })),
    f
  );
})(document as IndexedDocument);

/** The DOM nodes the player drives, looked up once in {@link VideoPlayer.bindControls}. */
interface ControlButtons {
  playBtnMobile: HTMLButtonElement;
  playBtnDesktop: HTMLButtonElement;
  closeBtn: HTMLButtonElement | null;
  subtitleListMobile: HTMLUListElement;
  audioListMobile: HTMLUListElement;
  subtitleListWrap: HTMLElement | null;
  audioListWrapMobile: HTMLDialogElement;
  shareMovieBtn: HTMLButtonElement | null;
  subtitleSizeBtn: HTMLButtonElement | null;
  subtitleSizeMenu: HTMLElement;
  subtitleSizeSlider: HTMLInputElement;
  subtitleSizeIncrement: HTMLButtonElement;
  subtitleSizeDecrement: HTMLButtonElement;
  audioListBtn: HTMLButtonElement;
  fullScreenBtn: HTMLButtonElement | null;
  fullScreenBtnDesktop: HTMLButtonElement | null;
  theaterModeBtn: HTMLButtonElement | null;
  scrubberArea: HTMLElement;
  scrubber: HTMLElement;
  scrubberBackground: HTMLElement;
  scrubberProgress: HTMLElement;
  scrubberButton: HTMLElement;
  timeStampCurrent: HTMLElement;
  timeStampDuration: HTMLElement;
  previewDuration: HTMLElement;
  volumeSlider: HTMLInputElement;
  volumeBtn: HTMLButtonElement;
  videoControls: HTMLElement;
  playbackRatesSlider: HTMLInputElement;
  playbackRateBtn: HTMLButtonElement | null;
  playbackRateListWrap: HTMLElement;
  playbackRateDecrement: HTMLButtonElement;
  playbackRateIncrement: HTMLButtonElement;
  playbackRateDisplay: HTMLElement;
  skipIntroBtn: HTMLButtonElement | null;
  seek10sForwardBtn: HTMLButtonElement;
  seek10sRewindBtn: HTMLButtonElement;
}

type IntervalName = 'soundFadeInterval' | 'skipIntroInterval' | 'loadNewAudioInterval';

interface VideoPlayerState {
  trackName: string;
  isPlaying: boolean;
  isPaused: boolean;
  isVideoEnded: boolean;
  isAudioEnded: boolean;
  playBackRate: number;
  isMute: boolean;
  isSeeking: boolean;
  startSeek: (ev: MouseEvent | TouchEvent) => void;
  stopSeek: (ev: MouseEvent | TouchEvent) => void;
  video: {
    isLoading: boolean;
    isLoaded: boolean;
  };
  subtitles: {
    enabled: boolean;
    size: number;
    sizes: number[];
    activeLanguage: string;
  };
  audio: {
    activeLanguage: string;
  };
  isFullScreen: boolean;
  isMouseActiveOnVideo: null;
  isKeyboardPressActiveOnVideo: null;
  isError: boolean;
  isUnloaded?: boolean;
}

export default class VideoPlayer {
  declare config: ResolvedVideoPlayerConfig;

  declare video: ILTPlayer | null;

  declare controlButtons: ControlButtons;

  declare prevVolume: number;

  declare wakeLock: WakeLockSentinel | null;

  declare soundFadeInterval: ReturnType<typeof setInterval> | null;

  declare skipIntroInterval: ReturnType<typeof setInterval> | null;

  declare loadNewAudioInterval: ReturnType<typeof setInterval> | null;

  declare state: VideoPlayerState;

  declare audioHandler: AudioHandler;

  declare videoDOM: VideoDOM;

  declare statistics: Statistics;

  declare pageHideHandler?: () => void;

  declare keyDownHandler?: (e: KeyboardEvent) => void;

  declare isMouseActiveOnVideo?: ReturnType<typeof setTimeout>;

  declare isKeyboardPressActiveOnVideo?: ReturnType<typeof setTimeout>;

  declare _doubleTapFeedbackTimer?: Partial<Record<'rewind' | 'forward', ReturnType<typeof setTimeout>>>;

  constructor(config: VideoPlayerConfig) {
    // The per-instance DOM ids and device flags are filled in by init().
    this.config = config as ResolvedVideoPlayerConfig;
    // Both are optional in the public config; determAudioLanguage()/determSubtitlesLanguage() below
    // narrow them to a language that actually exists, so default them to '' first.
    this.config.activeAudioTrack = config.activeAudioTrack ?? '';
    this.config.activeSubtitleTrack = config.activeSubtitleTrack ?? '';
    this.video = null;
    this.controlButtons = {} as ControlButtons;
    this.prevVolume = config.initialVolume ?? 0.5;
    this.config.skipIntroTime = config.skipIntroTime;
    this.wakeLock = null;
    this.soundFadeInterval = null;
    this.skipIntroInterval = null;
    this.loadNewAudioInterval = null;
    this.state = {
      trackName: '',
      isPlaying: false,
      isPaused: false,
      isVideoEnded: false,
      isAudioEnded: false,
      playBackRate: 1,
      isMute: false,
      isSeeking: false,
      startSeek: this.startSeek.bind(this),
      stopSeek: this.stopSeek.bind(this),
      video: {
        isLoading: false,
        isLoaded: false,
      },
      subtitles: {
        enabled: true,
        size: 1,
        sizes: [0.75, 1, 1.5, 2],
        activeLanguage: this.config.activeSubtitleTrack,
      },
      audio: {
        activeLanguage: this.config.activeAudioTrack,
      },
      isFullScreen: false,
      isMouseActiveOnVideo: null,
      isKeyboardPressActiveOnVideo: null,
      isError: false,
    };
    this.determAudioLanguage();
    this.determSubtitlesLanguage();
    this.audioHandler = new AudioHandler(config.onVolumeChange);
    this.videoDOM = new VideoDOM();
    this.statistics = new Statistics(
      config.videoUid,
      config.categoryUid,
      config.areaUid,
      config.userIdentity,
      // via this.config: same object, but the active track is defaulted/resolved by now
      this.config.activeAudioTrack,
      config.activityStreamEndpoint,
      config.token,
      config.getAccessToken,
      config.application,
      config.applicationVersion,
      config.siteLanguage,
      (slice) => this.handleVideoSlicePlayed(slice),
    );
  }

  // Identifies a single view of the video, and is sent with every video slice. Exposed so the
  // surrounding app can tag its own events with the same id.
  getSessionId() {
    return this.statistics.state.uuid;
  }

  handleVideoSlicePlayed(slice: VideoSlicePlayed) {
    if (!this.config.onVideoSlicePlayed) {
      return;
    }
    const durationSeconds = this.video ? this.video.duration() : undefined;
    this.config.onVideoSlicePlayed({
      ...slice,
      playbackRate: String(this.state.playBackRate),
      subtitleSize: String(this.state.subtitles.size),
      videoDurationSeconds: Number.isFinite(durationSeconds) ? durationSeconds : undefined,
    });
  }

  releaseWakeLock() {
    if (this.wakeLock !== null) {
      this.wakeLock.release();
      this.wakeLock = null;
    }
  }

  async requestWakeLock() {
    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
    } catch (err) {
      console.error('Failed to request wake lock:', err);
    }
  }

  init() {
    Howler.unload();

    this.addExternalScript('https://teams.microsoft.com/share/launcher.js');
    // Generate a random ID
    this.config.videoID = 'v' + Math.random().toString(36).substring(2, 8) + Math.random().toString(36).substring(2, 8);
    this.config.videoWrapID = 'vw' + this.config.videoID.substring(1, 255);
    this.config.fullscreenWrapID = 'fw' + this.config.videoID.substring(1, 255);
    this.config.iOsDevice = this.iOsDevice();

    this.isMobileOrIpad();

    // Preload audiotracks
    if (this.config.audioTracks) {
      this.loadAudio();
    }

    // Generate HTML
    return this.populateDOM();
  }

  load() {
    // Set up and bind video-js to DOM
    this.loadVideo();

    // Bind controls and events for this instance
    this.bindControls();

    // Set up event listeners for the SeekBar
    this.initSeekBar();

    if (this.prevVolume !== 0) {
      this.soundOn();
    } else {
      this.state.isMute = true;
      this.mute();
    }

    if (window.innerWidth < 1025) {
      const body = document.body;
      body.classList.add('touch');
    }

    // prefer pagehide to unload events
    // only register beforeunload/unload in browsers that don't support
    // pagehide to avoid breaking bfcache
    this.pageHideHandler = () => this.unload();
    if ('onpagehide' in self) {
      window.addEventListener('pagehide', this.pageHideHandler, { capture: true });
    } else {
      window.addEventListener('unload', this.pageHideHandler, { capture: true });
      window.addEventListener('beforeunload', this.pageHideHandler, { capture: true });
    }
  }

  unload() {
    if (this.state.isUnloaded) {
      return;
    }
    this.state.isUnloaded = true;

    if (this.keyDownHandler) {
      window.removeEventListener('keydown', this.keyDownHandler);
    }

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler, { capture: true });
      window.removeEventListener('unload', this.pageHideHandler, { capture: true });
      window.removeEventListener('beforeunload', this.pageHideHandler, { capture: true });
    }

    if (this.statistics && this.video) {
      this.statistics.sessionEnded(this.state.isPlaying, this.video.currentTime()!);
    }

    // Stop before destroying the tracks and player these intervals read from
    this.stopInterval('soundFadeInterval');
    this.stopInterval('skipIntroInterval');
    this.stopInterval('loadNewAudioInterval');

    if (this.audioHandler) {
      this.audioHandler.destroy();
    }

    // Dispose the video.js player.
    if (this.video && !this.video.isDisposed()) {
      this.video.dispose();
    }
    this.video = null;
  }

  isAudioLanguageAvailable(language: string | undefined) {
    return this.config.audioTracks.find((track) => track.langCode === language);
  }
  determAudioLanguage() {
    if (this.isAudioLanguageAvailable(this.state.audio.activeLanguage) || this.config.audioTracks.length === 0) {
      return;
    }
    if (this.isAudioLanguageAvailable(this.config.primaryLanguage)) {
      this.state.audio.activeLanguage = this.config.primaryLanguage!;
      this.config.activeAudioTrack = this.config.primaryLanguage!;
    } else {
      this.state.audio.activeLanguage = this.config.audioTracks[0].langCode;
      this.config.activeAudioTrack = this.config.audioTracks[0].langCode;
    }
  }

  determSubtitlesLanguage() {
    const activeSubtitle = this.config.subtitleTracks.find(
      (subtitle) => subtitle.langCode === this.state.subtitles.activeLanguage,
    );
    if (!activeSubtitle) {
      this.config.activeSubtitleTrack = '';
      this.state.subtitles.activeLanguage = '';
    }
  }

  populateDOM() {
    this.videoDOM.setConfig(this.config);
    return this.videoDOM.writeDOM();
  }

  loadAudio() {
    this.audioHandler.setTrackName(this.config.trackName!);
    this.audioHandler.setActiveAudioTrack(this.config.activeAudioTrack);
    this.audioHandler.addAudioTracks(this.config.audioTracks);
    this.state.audio.activeLanguage = this.config.activeAudioTrack; // Do i need this?
    this.audioHandler.audioTracks[this.audioHandler.activeAudioTrack].load();
  }

  loadVideo() {
    // Build sources array with HLS as primary and WebM as fallback
    const sources = [
      {
        src: this.config.hls,
        type: 'application/x-mpegURL',
        withCredentials: false,
      },
    ];

    // Add WebM source as fallback if available
    if (this.config.webm) {
      sources.push({
        src: this.config.webm,
        type: 'video/webm',
        withCredentials: false,
      });
    }

    this.video = videojs(
      this.config.videoID,
      {
        controls: false,
        playbackRates: [0.5, 0.8, 1, 1.2, 1.5, 2],
        autoplay: false,
        fluid: true,
        muted: true,
        aspectRatio: '16:9',
        withCredentials: false,
        poster: this.config.poster,
        loadingSpinner: false,
        overrideNative: true,
        html5: {
          nativeTextTracks: false,
          hls: {
            overrideNative: true,
            withCredentials: false,
          },
        },
        sources: sources,
      },
      () => {
        this.bindSubtitleTracks();
        // set active subtitle
        if (this.state.subtitles.activeLanguage) {
          this.switchSubtitles(this.state.subtitles.activeLanguage);
        }
      },
    ) as ILTPlayer;

    // video.js labels its region landmark "Video Player" for every instance. Append the track name so
    // pages with several players don't end up with indistinguishable landmarks.
    if (this.config.trackName) {
      const playerEl = this.video.el();
      playerEl.setAttribute('aria-label', `${playerEl.getAttribute('aria-label')}: ${this.config.trackName}`);
    }

    this.video.one('loadedmetadata', () => {
      let duration = '00:00';

      if (this.video!.duration()) {
        duration = this.makeReadableTimestamp(this.video!.duration()!);
      }
      this.controlButtons.timeStampCurrent.innerHTML = `00:00`;
      this.controlButtons.timeStampDuration.innerHTML = duration;
      this.controlButtons.previewDuration.innerHTML = duration;
    });

    // show poster at the end
    this.video.on('ended', () => {
      if (this.video!.isFullscreen()) {
        this.video!.exitFullscreen();
      }
      this.video!.poster(iltPoster);
      this.video!.hasStarted(false);
      this.state.isPlaying = false;
      this.statistics.onStopPlaying(this.video!.currentTime()!, 'end');
    });
  }

  bindSubtitleTracks() {
    // Add subtitle tracks
    const realSend = XMLHttpRequest.prototype.send;

    // Videojs doesnt support with credentials on addremotetexttrack
    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest) {
      this.withCredentials = false;
      realSend.apply(this, arguments as unknown as [body?: XMLHttpRequestBodyInit | null]);
    };
    const tracks = this.video!.textTracks();

    // Video.js hardcodes translate="yes" on the text track display container, which overrides
    // any root-level translate="no". Override it back here so browser translation is suppressed.
    const textTrackDisplay = this.video!.getChild('textTrackDisplay');
    if (textTrackDisplay) {
      textTrackDisplay.setAttribute('translate', 'no');
      textTrackDisplay.addClass('notranslate');
    }

    this.config.subtitleTracks.forEach((lang) => {
      this.video!.addRemoteTextTrack(
        {
          src: lang.subtitlePath,
          label: lang.language,
          language: lang.langCode,
        },
        false,
      );
    });

    for (let i = 0; i < tracks.length; i++) {
      // NOTE: text tracks expose `language`, not `langCode` — this comparison never matches.
      // Preserved as-is from the JS version; the active track is set by switchSubtitles().
      if ((tracks[i] as unknown as { langCode?: string }).langCode === this.config.activeSubtitleTrack) {
        tracks[i].mode = 'showing';
      }
    }
  }

  getElement<T extends HTMLElement = HTMLElement>(elm: string) {
    return document.getElementById(`${elm}_${this.config.videoID}`) as T | null;
  }

  /**
   * Show a brief visual feedback overlay for double-tap seek actions.
   */
  showDoubleTapFeedback(direction: 'rewind' | 'forward') {
    const id = direction === 'rewind' ? 'double-tap-rewind' : 'double-tap-forward';
    const el = this.getElement(id);
    if (!el) {
      return;
    }
    el.classList.remove('double-tap-overlay--active');
    // Force reflow so re-adding the class triggers the animation again
    void el.offsetWidth;
    el.classList.add('double-tap-overlay--active');
    clearTimeout(this._doubleTapFeedbackTimer?.[direction]);
    if (!this._doubleTapFeedbackTimer) {
      this._doubleTapFeedbackTimer = {};
    }
    this._doubleTapFeedbackTimer[direction] = setTimeout(() => {
      el.classList.remove('double-tap-overlay--active');
    }, 700);
  }

  /**
   * Attach double-tap-to-seek listeners on the video wrapper.
   * Only active on touch-capable devices (isMobileOrIpad).
   * Left half → rewind 10 s; right half → skip forward 10 s.
   *
   * This handler runs in the capture phase and always calls stopPropagation so
   * that the bubble-phase toggleControls/showControls listener never fires
   * directly from a tap here. The single-tap controls-toggle is instead deferred
   * into a timer callback so it only runs if no second tap arrives within 300 ms.
   * On double-tap the timer is cancelled and the seek runs without toggling controls.
   */
  bindDoubleTapSeek(videoWrapper: HTMLElement) {
    if (!this.config.isMobileOrIpad) {
      return;
    }

    const DOUBLE_TAP_DELAY_MS = 350;
    let lastTapTime = 0;
    let doubleTapTimer: ReturnType<typeof setTimeout> | null = null;

    videoWrapper.addEventListener(
      'touchend',
      (evt) => {
        // Ignore taps on the scrubber area or any control button — those have their own event handling and must not be treated as seek gestures.
        const target = evt.target as HTMLElement;
        if (
          target.closest('.scrubber-area') ||
          target.closest('.video-controls__button') ||
          target.closest('.video-controls__context-menu') ||
          target.closest('.video-controls__skip-intro')
        ) {
          return;
        }

        // Always stop propagation so the bubble-phase touchend handler (which calls toggleControls/showControls) never fires from a tap in this zone.
        // Single-tap control toggling is handled inside the timer callback below.
        // Always prevent default to suppress the synthetic click event the browser generates after touchend —
        // without this, the first tap of a double-tap fires a click ~300ms later and triggers play/pause via the click handler.
        evt.stopPropagation();
        evt.preventDefault();

        const now = Date.now();
        const timeSinceLastTap = now - lastTapTime;

        if (timeSinceLastTap < DOUBLE_TAP_DELAY_MS && timeSinceLastTap > 0) {
          // Double-tap confirmed — cancel the pending single-tap controls-toggle and perform the seek without touching control visibility.
          clearTimeout(doubleTapTimer!);
          doubleTapTimer = null;
          lastTapTime = 0;

          const touch = evt.changedTouches[0];
          const wrapperRect = videoWrapper.getBoundingClientRect();
          const tapX = touch.clientX - wrapperRect.left;
          const isLeftHalf = tapX < wrapperRect.width / 2;
          const duration = this.video!.duration() || 0;
          this.state.isSeeking = true;

          if (isLeftHalf) {
            const newTime = Math.max(0, Math.min(duration, this.video!.currentTime()! - 10));
            this.video!.currentTime(newTime);
            this.syncAudio();
            this.showDoubleTapFeedback('rewind');
          } else {
            const newTime = Math.max(0, Math.min(duration, this.video!.currentTime()! + 10));
            this.video!.currentTime(newTime);
            this.syncAudio();
            this.showDoubleTapFeedback('forward');
          }

          requestAnimationFrame(() => {
            this.state.isSeeking = false;
          });
        } else {
          // First tap — record the time and defer the single-tap controls-toggle so that a second tap arriving within 300 ms can cancel it.
          lastTapTime = now;
          doubleTapTimer = setTimeout(() => {
            doubleTapTimer = null;
            lastTapTime = 0;
            // Replicate the bubble-phase single-tap controls-visibility logic that was suppressed by stopPropagation.
            // Only show/hide the overlay here — never trigger play/pause.
            // The poster-click-to-play path is handled by the bubble-phase touchend listener on videoWrapper.
            if (this.state.isPaused) {
              this.showControls();
            } else {
              this.controlButtons.scrubber.classList.add('is-expanded');
              this.toggleControls(videoWrapper);
            }
          }, DOUBLE_TAP_DELAY_MS);
        }
      },
      { capture: true },
    );
  }

  bindControls() {
    this.controlButtons = {
      playBtnMobile: this.getElement<HTMLButtonElement>('play_mobile'),
      playBtnDesktop: this.getElement<HTMLButtonElement>('play_desktop'),
      closeBtn: this.getElement<HTMLButtonElement>('close'),
      subtitleListMobile: this.getElement<HTMLUListElement>('subtitle-language-mobile'),
      audioListMobile: this.getElement<HTMLUListElement>('audio-language-mobile'),
      subtitleListWrap: this.getElement('subtitle-language-wrapper'),
      audioListWrapMobile: this.getElement<HTMLDialogElement>('audio-language-wrapper-mobile'),
      shareMovieBtn: this.getElement<HTMLButtonElement>('share'),
      subtitleSizeBtn: this.getElement<HTMLButtonElement>('subtitle-size'),
      subtitleSizeMenu: this.getElement('subtitle-size-menu'),
      subtitleSizeSlider: this.getElement<HTMLInputElement>('subtitle-size-slider'),
      subtitleSizeIncrement: this.getElement<HTMLButtonElement>('subtitle-size-increment'),
      subtitleSizeDecrement: this.getElement<HTMLButtonElement>('subtitle-size-decrement'),
      audioListBtn: this.getElement<HTMLButtonElement>('audio-language-button'),
      fullScreenBtn: this.getElement<HTMLButtonElement>('fullscreen'),
      fullScreenBtnDesktop: this.getElement<HTMLButtonElement>('fullscreen_desktop'),
      theaterModeBtn: this.getElement<HTMLButtonElement>('theater-mode'),
      scrubberArea: this.getElement('scrubber-area'),
      scrubber: this.getElement('scrubber'),
      scrubberBackground: this.getElement('scrubber-background'),
      scrubberProgress: this.getElement('scrubber-progress'),
      scrubberButton: this.getElement('scrubber-button'),
      timeStampCurrent: this.getElement('timestampCurrent'),
      timeStampDuration: this.getElement('timestampDuration'),
      previewDuration: this.getElement('previewDuration'),
      volumeSlider: this.getElement<HTMLInputElement>('volumeslider'),
      volumeBtn: this.getElement<HTMLButtonElement>('volume'),
      videoControls: this.getElement('video-controls'),
      playbackRatesSlider: this.getElement<HTMLInputElement>('playback_slider'),
      playbackRateBtn: this.getElement<HTMLButtonElement>('playback'),
      playbackRateListWrap: this.getElement('playback_list_wrap'),
      playbackRateDecrement: this.getElement<HTMLButtonElement>('playbackrate-decrement'),
      playbackRateIncrement: this.getElement<HTMLButtonElement>('playbackrate-increment'),
      playbackRateDisplay: this.getElement('playbackrate-value'),
      skipIntroBtn: this.getElement<HTMLButtonElement>('video-controls__skip-intro'),
      seek10sForwardBtn: this.getElement<HTMLButtonElement>('double-tap-forward-btn'),
      seek10sRewindBtn: this.getElement<HTMLButtonElement>('double-tap-rewind-btn'),
    } as ControlButtons;

    const videoWrapper = document.getElementById(this.config.videoWrapID)!;
    const fullscreenWrapper = document.getElementById(this.config.fullscreenWrapID)!;

    this.controlButtons.videoControls.addEventListener('touchend', (evt) => {
      const videoControls = this.controlButtons.videoControls;
      const clickedElement = evt.target as HTMLElement;

      // if playing, and not clicking any actual control buttons, hide controls
      if (
        this.state.isPlaying &&
        (clickedElement === videoControls || clickedElement.parentElement === videoControls)
      ) {
        if (videoControls.classList.contains('is-active')) {
          this.closeAllMenus();
          videoControls.classList.remove('is-active');
          evt.stopPropagation();
          evt.preventDefault();
        }
      }
    });

    // Play button
    const playBtnClickHandler = () => {
      this.playOrPause();

      if (this.state.isPlaying) {
        this.statistics.onStartPlaying(this.video!.currentTime()!);
        if (this.config.onIsPlayingChange) {
          this.config.onIsPlayingChange(this.state.isPlaying);
        }
      } else if (this.state.isPaused) {
        this.statistics.onStopPlaying(this.video!.currentTime()!, 'pause');
        if (this.config.onIsPausedChange) {
          this.config.onIsPausedChange(this.state.isPaused, this.video!.currentTime()!.toString());
        }
      }
    };
    this.controlButtons.playBtnMobile.addEventListener('click', playBtnClickHandler);
    this.controlButtons.playBtnDesktop.addEventListener('click', playBtnClickHandler);

    // Seek 10s forward
    this.controlButtons.seek10sForwardBtn.addEventListener('click', () => {
      const duration = this.video!.duration() || 0;
      const newTime = Math.max(0, Math.min(duration, this.video!.currentTime()! + 10));
      this.video!.currentTime(newTime);
      this.syncAudio();
    });

    // Rewind 10s
    this.controlButtons.seek10sRewindBtn.addEventListener('click', () => {
      const duration = this.video!.duration() || 0;
      const newTime = Math.max(0, Math.min(duration, this.video!.currentTime()! - 10));
      this.video!.currentTime(newTime);
      this.syncAudio();
    });

    // Volume button
    this.controlButtons.volumeBtn.addEventListener('click', () => {
      this.muteOrPlay();
    });

    // Show volume slider on hover
    this.controlButtons.volumeBtn.addEventListener('mouseover', () => {
      !this.config.isMobileOrIpad && videoWrapper.querySelector('.volume-slider-container')!.classList.add('active');
    });
    this.controlButtons.volumeSlider.parentElement!.addEventListener('mouseleave', () => {
      if (!this.config.isMobileOrIpad) {
        setTimeout(() => videoWrapper.querySelector('.volume-slider-container')!.classList.remove('active'), 500);
      }
    });

    // Show volume slider on focus
    this.controlButtons.volumeSlider.addEventListener('focusin', () => {
      videoWrapper.querySelector('.volume-slider-container')!.classList.add('active');
    });
    this.controlButtons.volumeSlider.addEventListener('focusout', () => {
      setTimeout(() => videoWrapper.querySelector('.volume-slider-container')!.classList.remove('active'), 500);
    });

    // Volume slider
    this.controlButtons.volumeSlider.addEventListener('input', () => {
      this.audioHandler.setVolume(Number(this.controlButtons.volumeSlider.value) / 100);

      if (this.state.isMute) {
        this.muteOrPlay();
      }
    });

    // Toggle context menu
    this.controlButtons.audioListBtn.addEventListener('click', () => {
      this.controlButtons.audioListWrapMobile.classList.toggle('is-active');
      this.syncZIndexForContainer();
      this.toggleAccessibilityForDialog(this.controlButtons.audioListWrapMobile, true);
      // prevent window from scrolling in the background on mobile
      if (window.innerWidth < 1024) {
        document.body.classList.add('is-reveal-open');
        this.controlButtons.audioListWrapMobile.showModal();
      }
    });

    // Playback menu
    this.controlButtons.playbackRateBtn &&
      this.controlButtons.playbackRateBtn.addEventListener('click', () => {
        if (!this.controlButtons.playbackRateListWrap.classList.contains('is-active')) {
          this.closeAllMenus();
          this.toggleAccessibilityForDialog(this.controlButtons.playbackRateListWrap, false);
        }
        this.controlButtons.playbackRateListWrap.classList.toggle('is-active');
        this.syncZIndexForContainer();
        this.toggleAccessibilityForDialog(this.controlButtons.playbackRateListWrap, true);
      });
    const setPlaybackRateDisplay = (playbackRate: number) => {
      this.controlButtons.playbackRateDisplay.textContent = `(${playbackRate}x)`;
    };
    setPlaybackRateDisplay(1);
    this.controlButtons.playbackRatesSlider.addEventListener('change', () => {
      const index = Number(this.controlButtons.playbackRatesSlider.value);
      // Use the index to get the actual playback rate
      const playbackRate = playbackRates[index];
      setPlaybackRateDisplay(playbackRate);
      this.video!.playbackRate(playbackRate);
      this.setPlaybackRate(playbackRate);
    });

    // Define the allowed playback rates
    const playbackRates = this.config.playbackRates;

    this.controlButtons.playbackRateDecrement.addEventListener('click', () => {
      this.controlButtons.playbackRatesSlider.stepDown();
      this.controlButtons.playbackRatesSlider.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.controlButtons.playbackRateIncrement.addEventListener('click', () => {
      this.controlButtons.playbackRatesSlider.stepUp();
      this.controlButtons.playbackRatesSlider.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Close button - mobile context menu
    this.controlButtons.videoControls.querySelectorAll('.close').forEach((closeBtn) =>
      closeBtn.addEventListener('click', () => {
        this.closeAllMenus();
        document.body.classList.remove('is-reveal-open');
      }),
    );

    // Close menus on esc
    document.addEventListener(
      'keyup',
      (e) => {
        // Escape keypress
        if (e.keyCode === 27) {
          this.closeAllMenus();
          this.hideShare();
          document.body.classList.remove('is-reveal-open');
        }
      },
      true /* grab event on tunnel, not on bubble */,
    );

    this.keyDownHandler = (e) => {
      const focused = document.activeElement as HTMLElement | null;
      if (focused && (focused.matches('input[type="range"], input, select, textarea') || focused.isContentEditable)) {
        return;
      }

      const videoPlayer = this;

      // Get video-wrapper, which is the parent of the videoPlayer
      const videoWrapper = videoPlayer.video!.el().parentElement!;
      const isVideoFocused = document.activeElement === videoWrapper || videoWrapper.contains(document.activeElement);

      if (!isVideoFocused && document.activeElement !== document.body) {
        return; // Ignore the event if the video player isn't focused, with exception for the body element (so that users can play the video directly when entering the page)
      }

      // Rewind video 10s on left arrow keypress
      if (e.keyCode === 37) {
        e.preventDefault();
        this.showControls();

        videoPlayer.video!.currentTime(videoPlayer.video!.currentTime()! - 10);
        this.syncAudio();

        let hideControls: ReturnType<typeof setTimeout> | null = null;

        if (hideControls != null) {
          clearTimeout(hideControls);
        }

        const that = this;
        hideControls = setTimeout(function () {
          that.hideControls();
        }, 3000);
      }

      // Jump 10s forward on right arrow keypress
      if (e.keyCode === 39) {
        e.preventDefault();
        this.showControls();

        this.video!.currentTime(this.video!.currentTime()! + 10);
        this.syncAudio();

        let hideControls: ReturnType<typeof setTimeout> | null = null;

        if (hideControls != null) {
          clearTimeout(hideControls);
        }

        const that = this;
        hideControls = setTimeout(function () {
          that.hideControls();
        }, 3000);
      }

      // Play/pause video on space keypress
      if (e.keyCode === 32) {
        if (e.target === this.controlButtons.skipIntroBtn) {
          return;
        }
        e.preventDefault();
        this.showControls();
        this.playOrPause();
      }
    };
    window.addEventListener('keydown', this.keyDownHandler);

    // Show video controls on hover
    videoWrapper.addEventListener('mouseenter', () => {
      if (window.innerWidth > 1024) {
        this.showControls();
      }
    });

    // Desktop - Toggle video controls and start/pause on video and poster click
    videoWrapper.addEventListener('click', (evt) => {
      evt.preventDefault();
      const targetElement = evt.target as HTMLElement;

      if (
        targetElement.tagName.toLowerCase() === 'video' ||
        targetElement.classList.contains('vjs-poster') ||
        targetElement.classList.contains('video-controls')
      ) {
        // Trigger PlayPause function on Play/Pause button
        this.toggleControls(targetElement);
        this.controlButtons.playBtnDesktop.click();
      }
    });

    // Mobile/Tablet - Toggle video controls and start play on poster click
    videoWrapper.addEventListener('touchend', (evt) => {
      if (this.state.isPaused) {
        this.showControls();
      } else {
        const targetElement = evt.target as HTMLElement;
        this.controlButtons.scrubber.classList.add('is-expanded');
        this.toggleControls(targetElement);
      }
      if ((evt.target as HTMLElement).classList.contains('vjs-poster')) {
        // Trigger PlayPause function on Play/Pause button
        this.controlButtons.playBtnMobile.click();
      }
    });

    // Remove contextmenu in Firefox
    videoWrapper.addEventListener('contextmenu', (evt) => {
      if (videojs.browser.IS_FIREFOX) {
        evt.preventDefault();
      }
    });

    // Mobile/Tablet - Hide video controls on click outside
    document.addEventListener('click', (evt) => {
      const targetElement = evt.target as HTMLElement;
      this.hideControls(targetElement);
      this.hideShare(evt);
    });

    // Desktop - Hide video controls on hover outside
    document.addEventListener('mousemove', (evt) => {
      // show-for-medium class from foundation works with this size
      // Also checking if the devise is not mobile,
      // the logic for the mobile devices shouldn't be based on "mousemove" event.
      if (window.innerWidth > 640 && !this.config.isMobileOrIpad) {
        let targetElement = evt.target as Node | null;
        const hoveredScrubber = this.controlButtons.scrubber.classList.contains('is-expanded');

        do {
          if (targetElement === videoWrapper) {
            clearTimeout(this.isMouseActiveOnVideo);
            this.isMouseActiveOnVideo = setTimeout(() => {
              // Hide controls: Mouse has been idle for 2000 milliseconds and is not hovering scrubber
              if (!hoveredScrubber) {
                this.hideControls();
              }
            }, 2000);
            // Show controls: Mouse is moving and controls are hidden
            if (!this.controlButtons.videoControls.classList.contains('is-active')) {
              this.showControls();
            }
            return;
          }
          // Go up the DOM
          targetElement = targetElement!.parentNode;
        } while (targetElement);

        // Hover outside.
        if (
          this.state.isPlaying ||
          (this.state.isPaused && this.controlButtons.videoControls.classList.contains('is-active'))
        ) {
          if (evt.target !== this.controlButtons.videoControls) {
            !this.state.isSeeking && this.hideControls(evt.target as HTMLElement);
          }
        }
      }
    });

    // Show controls on focus in
    videoWrapper.addEventListener('focusin', () => {
      this.showControls();
    });
    // Hide video controls on focus out
    videoWrapper.addEventListener('focusout', () => {
      clearTimeout(this.isKeyboardPressActiveOnVideo);
      this.isKeyboardPressActiveOnVideo = setTimeout(() => {
        // Hide controls: Keyboard has been idle for 2000 milliseconds
        if (this.controlButtons.videoControls.classList.contains('is-active')) {
          this.hideControls();
        }
      }, 3000);
    });

    // Pick Audio Language Mobile
    const audioItems = this.controlButtons.audioListMobile.children;
    for (let index = 0; index < audioItems.length; index++) {
      const listItem = audioItems[index];
      listItem.addEventListener('click', (ev) => {
        // Target element can be the <button> or the <li> element
        // <li> is the audioItem element which contains the attributes
        const target = ev.target as HTMLElement;
        const audioItem = target.hasAttribute('data-type') ? target : target.parentElement!;
        if (audioItem.classList.contains('disabled')) {
          console.log('disabled: ', audioItem.contains('disabled' as unknown as Node));
          return;
        }

        // Remove active class from all items
        for (let i = 0; i < audioItems.length; i++) {
          const item = audioItems[i];
          item.classList.remove('active');
        }

        audioItem.className += ' active';

        if (this.config.onActiveVideoLanguageChange) {
          this.config.onActiveVideoLanguageChange({
            type: 'audio',
            lang: audioItem.getAttribute('data-lang'),
          });
        }
        this.switchAudio(audioItem.getAttribute('data-lang')!);
      });
    }

    if (this.controlButtons.skipIntroBtn) {
      this.controlButtons.skipIntroBtn.addEventListener('click', () => {
        this.play(true);
      });
      this.skipIntroInterval = setInterval(() => {
        if (this.video?.currentTime() && this.video.currentTime()! >= this.config.skipIntroTime!) {
          this.controlButtons.skipIntroBtn!.remove();
          this.stopInterval('skipIntroInterval');
        }
      }, 1000);
    }

    // Subtitle size menu
    if (this.controlButtons.subtitleSizeBtn) {
      this.controlButtons.subtitleSizeBtn.addEventListener('click', () => {
        this.controlButtons.subtitleSizeMenu.classList.toggle('is-active');
        this.syncZIndexForContainer();
        this.toggleAccessibilityForDialog(this.controlButtons.subtitleSizeMenu, true);
      });

      this.controlButtons.subtitleSizeSlider.addEventListener('change', () => {
        const index = Number(this.controlButtons.subtitleSizeSlider.value);
        // Use the index to get the actual playback rate
        const subtitleSize = this.state.subtitles.sizes[index];

        this.state.subtitles.size = subtitleSize;
        if (subtitleSize === 1) {
          this.video!.textTrackSettings.setDefaults();
        } else {
          this.video!.textTrackSettings.setValues({
            fontPercent: subtitleSize,
          });
        }
        this.video!.textTrackSettings.updateDisplay();
      });

      this.controlButtons.subtitleSizeDecrement.addEventListener('click', () => {
        this.controlButtons.subtitleSizeSlider.stepDown();
        this.controlButtons.subtitleSizeSlider.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.controlButtons.subtitleSizeIncrement.addEventListener('click', () => {
        this.controlButtons.subtitleSizeSlider.stepUp();
        this.controlButtons.subtitleSizeSlider.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (this.controlButtons.shareMovieBtn) {
      this.controlButtons.shareMovieBtn.addEventListener('click', () => {
        const shareVideoContainer = document.querySelector('.share-video')!;
        shareVideoContainer.classList.toggle('is-share');
        if (this.config.onShareBtnClick) {
          this.config.onShareBtnClick();
        }

        const shareUrl = shareVideoContainer.querySelector<HTMLInputElement>('.input-url')!;
        shareUrl.select();
        document.execCommand('copy');
      });
    }

    // Pick Subtitle Language Mobile
    const subtitleItems = this.controlButtons.subtitleListMobile.children;
    for (let index = 0; index < subtitleItems.length; index++) {
      const listItem = subtitleItems[index];
      listItem.addEventListener('click', (ev) => {
        // Target element can be the <button> or the <li> element
        // <li> is the subtitleItem element which contains the attributes
        const target = ev.target as HTMLElement;
        const subtitleItem = target.hasAttribute('data-type') ? target : target.parentElement!;
        if (subtitleItem.classList.contains('disabled')) {
          return;
        }

        // Remove active class from all items
        for (let i = 0; i < subtitleItems.length; i++) {
          const item = subtitleItems[i];
          item.classList.remove('active');
        }

        subtitleItem.className += ' active';

        if (this.config.onActiveVideoLanguageChange) {
          this.config.onActiveVideoLanguageChange({
            type: 'subtitle',
            lang: subtitleItem.getAttribute('data-lang'),
          });
        }
        this.switchSubtitles(subtitleItem.getAttribute('data-lang')!);
      });
    }

    const toggleFullscreen = () => {
      if (screenfull.isEnabled) {
        if (!screenfull.isFullscreen) {
          fullscreenWrapper.classList.add('fullscreen');
          videoWrapper.classList.add('is-fullscreen');
          videoWrapper.classList.remove('is-theater');
          screenfull.request(fullscreenWrapper);
          document.getElementById(this.config.videoID)!.focus();
          this.state.isFullScreen = true;
          if ((screen.orientation as LockableScreenOrientation).lock) {
            (screen.orientation as LockableScreenOrientation).lock!('landscape').catch(() => {});
          }
        } else {
          fullscreenWrapper.classList.remove('fullscreen');
          videoWrapper.classList.remove('is-fullscreen');
          screenfull.exit();
          this.state.isFullScreen = false;
          if (this.config.isMobileOrIpad) {
            this.pause();
          }
          if (screen.orientation.unlock) {
            screen.orientation.unlock();
          }
        }
      } else {
        // Fallback for iPhone(safari) that does not support Fullscreen API.
        if (!this.state.isFullScreen) {
          fullscreenWrapper.classList.add('fullscreen');
          videoWrapper.classList.add('is-fullscreen');
          videoWrapper.classList.remove('is-theater');
          document.getElementById(this.config.videoID)!.focus();
          this.state.isFullScreen = true;
        } else {
          fullscreenWrapper.classList.remove('fullscreen');
          videoWrapper.classList.remove('is-fullscreen');
          this.state.isFullScreen = false;
          if (this.config.isMobileOrIpad) {
            this.pause();
          }
        }
      }
    };

    // Fullscreen
    if (this.controlButtons.fullScreenBtn) {
      this.controlButtons.fullScreenBtn.addEventListener('click', toggleFullscreen);
      if (this.controlButtons.fullScreenBtnDesktop) {
        this.controlButtons.fullScreenBtnDesktop.addEventListener('click', toggleFullscreen);
      }

      document.addEventListener('fullscreenchange', () => {
        if (screenfull.isEnabled) {
          if (!screenfull.isFullscreen && this.state.isFullScreen) {
            // On Apple touch devices there is an alternative close button
            // Code below triggers alternative close button
            const fullscreenWrap = document.getElementById(this.config.fullscreenWrapID)!;
            fullscreenWrap.classList.remove('fullscreen');
            document.getElementById(this.config.videoWrapID)!.classList.remove('is-fullscreen');
            screenfull.exit();
            this.state.isFullScreen = false;
          }
        }

        if (this.controlButtons.theaterModeBtn) {
          this.controlButtons.theaterModeBtn.classList.toggle('hide-theatre-button');
        }
      });
    }

    this.video!.on('ended', () => {
      this.state.isVideoEnded = true;
      this.releaseWakeLock();
      if (this.state.isVideoEnded && this.state.isAudioEnded) {
        this.pause();
        this.config.onVideoEnded?.();
      }
      if (this.config.onVideoEnd) {
        this.config.onVideoEnd();
      }
    });

    this.video!.on('play', () => {
      this.requestWakeLock();
      if (videojs.browser.TOUCH_ENABLED) {
        this.state.isPaused && this.play();
      }
      // Go into fullscreen automatically on play if on mobile
      if (this.config.isMobileOrIpad && !this.state.isFullScreen) {
        toggleFullscreen();
      }
    });

    /**
     * Trigger pause on(touch):
     * 1. Browser or tab minimized.
     * 2. Element not visible in dom
     */
    this.video!.on('pause', () => {
      this.releaseWakeLock();
      if (this.state.isSeeking) {
        return;
      }
      if (videojs.browser.TOUCH_ENABLED || document.getElementById(this.config.fullscreenWrapID) === null) {
        this.state.isPlaying && this.pause();
      }
    });

    this.video!.on('error', () => {
      this.state.isError = true;
      setTimeout(() => {
        this.pause();
      }, 200);
    });

    this.video!.reloadSourceOnError({
      errorInterval: 1,
    });

    this.audioHandler.audioTracks[this.audioHandler.activeAudioTrack].on('end', () => {
      this.state.isAudioEnded = true;
      if (this.state.isVideoEnded && this.state.isAudioEnded) {
        this.pause();
        this.config.onVideoEnded?.();
      }
    });

    // Theater mode
    if (this.controlButtons.theaterModeBtn) {
      this.controlButtons.theaterModeBtn.addEventListener('click', () => {
        const videoWrapper = document.getElementById(this.config.videoWrapID)!;

        fullscreenWrapper.classList.toggle('is-theater');
        videoWrapper.classList.toggle('is-theater');
        const doScrollToTheater = videoWrapper.classList.contains('is-theater');

        if (!doScrollToTheater) {
          return false;
        }

        // Scroll into center of viewport. Need to wait for .video-wrap transition to complete to obtain videoWrapper height
        setTimeout(() => {
          window.scrollTo({
            top: videoWrapper.getBoundingClientRect().height - window.innerHeight / 2,
            left: 0,
            behavior: 'smooth',
          });
        }, 350);
      });
    }

    // Double-tap to seek (mobile only)
    this.bindDoubleTapSeek(videoWrapper);
  }

  iOsDevice(): IOsDevice {
    const CHECK_USER_AGENT = (window.navigator && window.navigator.userAgent) || '';
    const CHECK_IS_IPAD =
      /iPad/i.test(CHECK_USER_AGENT) ||
      (videojs.browser.IS_SAFARI && videojs.browser.TOUCH_ENABLED && !/iPhone/i.test(CHECK_USER_AGENT));
    const CHECK_IS_IPHONE = /iPhone/i.test(CHECK_USER_AGENT) && !CHECK_IS_IPAD;
    return {
      ipad: CHECK_IS_IPAD,
      iphone: CHECK_IS_IPHONE,
    };
  }

  isMobileOrIpad() {
    const ua = window.navigator.userAgent;
    // Check for iPad with iOS
    if (ua.includes('iPad')) {
      this.config.isMobileOrIpad = true;
      return true;
    }

    // Check for mobile
    if (videojs.browser.IS_ANDROID || videojs.browser.IS_IPHONE) {
      this.config.isMobileOrIpad = true;
      return true;
    }

    // Check for iPad with iPadOS
    if (ua.includes('Macintosh')) {
      try {
        document.createEvent('TouchEvent');
        this.config.isMobileOrIpad = true;
        return true;
      } catch (e) {}
    }

    this.config.isMobileOrIpad = false;
    return false;
  }

  toggleTheatre(theatreMode: boolean) {
    document.getElementById(this.config.videoWrapID)!.classList.toggle('is-theater');
    const theaterContainerWidth = document
      .getElementById(this.config.theatreContainerID!)!
      .getBoundingClientRect().width;

    if (!theatreMode) {
      document.getElementById(this.config.videoWrapID)!.style.width = theaterContainerWidth as unknown as string;
      return false;
    }

    document.getElementById(this.config.videoWrapID)!.style.width = '100%';
  }

  toggleControls(clickedElement: Node) {
    const videoControls = this.controlButtons.videoControls;
    const scrubber = this.controlButtons.scrubber;

    // Check if clicked element is not videocontrols before toggling
    if (clickedElement !== videoControls && !videoControls.contains(clickedElement) && this.state.isPlaying) {
      if (videoControls.classList.contains('is-active')) {
        videoControls.classList.remove('is-active');
        scrubber.classList.remove('is-active');
      } else {
        videoControls.classList.add('is-active');
        scrubber.classList.add('is-active');
      }
    }
  }

  showControls() {
    const videoControls = this.controlButtons.videoControls;
    const scrubber = this.controlButtons.scrubber;
    if (!videoControls.classList.contains('is-active')) {
      videoControls.classList.add('is-active');
      scrubber.classList.add('is-active');
    }
  }

  hideControls(clickedElement?: Node | null) {
    const videoControls = this.controlButtons.videoControls;
    const videoWrapper = document.getElementById(this.config.videoWrapID);
    const scrubber = this.controlButtons.scrubber;

    // Check if clicked element is not filmplayer before hiding or if user is using menus
    if (!videoWrapper || this.areMenusOpen()) {
      return false;
    }

    if (clickedElement !== videoWrapper && !videoWrapper.contains(clickedElement ?? null) && this.state.isPlaying) {
      if (videoControls.classList.contains('is-active')) {
        this.closeAllMenus();
        videoControls.classList.remove('is-active');
        scrubber.classList.remove('is-active');
      }
    }
  }

  hideShare(ev?: Event) {
    const shareVideoContainer = document.querySelector('.share-video');
    const shareOpen = shareVideoContainer && shareVideoContainer.classList.contains('is-share');

    const ignoreClass = ['icon-share', 'video-controls__button video-controls__button--share', 'input-url'];
    if (ev && ignoreClass.includes((ev.target as HTMLElement).className)) {
      return false;
    }
    shareOpen && shareVideoContainer.classList.toggle('is-share');
  }

  playOrPause() {
    if (this.state.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  async play(skipIntro = false) {
    const shouldSkipIntro =
      (this.config.autoSkipIntro || skipIntro) && this.video!.currentTime()! < this.config.skipIntroTime!;

    if (shouldSkipIntro) {
      this.video!.currentTime(this.config.skipIntroTime!);
      this.controlButtons.skipIntroBtn?.remove();
    }
    this.syncAudio();
    const playPromise = this.video!.play();
    if (playPromise !== undefined) {
      await playPromise;
    }
    if (shouldSkipIntro && !this.state.isMute) {
      this.soundOnFade();
    }
    this.audioHandler.play();
    this.state.isPlaying = true;
    this.state.isVideoEnded = false;
    this.state.isAudioEnded = false;
    this.state.isPaused = false;

    this.controlButtons.playBtnMobile.children[0].classList.remove('fa-play');
    this.controlButtons.playBtnMobile.children[0].classList.add('fa-pause');
    this.controlButtons.playBtnMobile.setAttribute(
      'aria-label',
      this.config.i18n ? this.config.i18n('film.pause') : 'Pausa',
    );

    this.controlButtons.playBtnDesktop.children[0].classList.remove('fa-play');
    this.controlButtons.playBtnDesktop.children[0].classList.add('fa-pause');
    this.controlButtons.playBtnDesktop.setAttribute(
      'aria-label',
      this.config.i18n ? this.config.i18n('film.pause') : 'Pausa',
    );

    const videoControls = this.controlButtons.videoControls;
    const scrubber = this.controlButtons.scrubber;

    this.statistics.sessionStarted();

    // hide control buttons
    setTimeout(() => {
      this.hideControls();
    }, 1700);
  }

  pause() {
    if (!this.state.isPlaying) {
      return;
    }
    this.state.isPlaying = false;
    this.state.isPaused = true;
    this.video!.pause();
    this.audioHandler.pause();
    this.controlButtons.playBtnMobile.children[0].classList.remove('fa-pause');
    this.controlButtons.playBtnMobile.children[0].classList.add('fa-play');
    this.controlButtons.playBtnMobile.setAttribute(
      'aria-label',
      `${this.config.i18n ? this.config.i18n('film.play') : 'Spela'} ${this.config.trackName}`,
    );

    this.controlButtons.playBtnDesktop.children[0].classList.remove('fa-pause');
    this.controlButtons.playBtnDesktop.children[0].classList.add('fa-play');
    this.controlButtons.playBtnDesktop.setAttribute(
      'aria-label',
      `${this.config.i18n ? this.config.i18n('film.play') : 'Spela'} ${this.config.trackName}`,
    );
  }

  restart() {
    this.video!.currentTime(0);
    this.play(false);
  }

  muteOrPlay() {
    if (this.state.isMute) {
      this.state.isMute = false;
      this.soundOn();
    } else {
      this.state.isMute = true;
      this.mute();
    }
  }

  setPlaybackRate(rate: number) {
    this.state.playBackRate = rate;
    this.audioHandler.setPlaybackRate(rate);
  }

  soundOn() {
    this.audioHandler.setMute(false);
    this.audioHandler.setVolume(this.prevVolume);
    this.controlButtons.volumeSlider.value = String(this.prevVolume * 100);
    this.controlButtons.volumeBtn.children[0].classList.remove('fa-volume-slash');
    this.controlButtons.volumeBtn.children[0].classList.add('fa-volume');
    this.controlButtons.volumeBtn.setAttribute(
      'aria-label',
      this.config.i18n ? this.config.i18n('film.soundOff') : 'Ljud av',
    );
  }

  soundOnFade() {
    const getTrack = () => this.audioHandler.audioTracks[this.audioHandler.activeAudioTrack];

    // No narrator audio for this track, so there is nothing to fade in
    if (!getTrack()) {
      this.soundOn();
      return;
    }

    this.audioHandler.setVolume(0);
    this.soundFadeInterval = setInterval(() => {
      const track = getTrack();
      // audioHandler.destroy() unloaded the tracks while the fade was still running
      if (!track) {
        this.stopInterval('soundFadeInterval');
        return;
      }
      if (track.volume() < this.prevVolume) {
        this.audioHandler.setVolume(track.volume() + 0.02);
        this.controlButtons.volumeSlider.value = String(track.volume() * 100);
      } else if (track.state() === 'loaded') {
        this.audioHandler.setVolume(this.prevVolume);
        this.soundOn();
        this.stopInterval('soundFadeInterval');
      }
    }, 50);
  }

  stopInterval(name: IntervalName) {
    if (this[name]) {
      clearInterval(this[name]);
      this[name] = null;
    }
  }

  mute() {
    this.prevVolume = Number(this.controlButtons.volumeSlider.value) / 100;
    this.audioHandler.setMute(true);
    this.controlButtons.volumeSlider.value = '1';
    this.controlButtons.volumeBtn.children[0].remove();
    this.controlButtons.volumeBtn.insertAdjacentHTML('afterbegin', '<span class="fa fa-volume-slash"></span>');
    this.controlButtons.volumeBtn.setAttribute(
      'aria-label',
      this.config.i18n ? this.config.i18n('film.soundOn') : 'Ljud på',
    );
  }

  switchSubtitles(lang: string) {
    const tracks = this.video!.textTracks();
    if (this.config.onSubtitleLanguageChange) {
      this.config.onSubtitleLanguageChange(lang);
    }
    for (let i = 0; i < tracks.length; i++) {
      // Disable track
      tracks[i].mode = 'disabled';

      // Find the selected track and enable it. Ignore empty(off) language
      if (tracks[i].language === lang && tracks[i].language !== '') {
        tracks[i].mode = 'showing';
      }

      this.state.subtitles.activeLanguage = lang;
    }
    this.statistics.changeLanguage({
      audio: this.state.audio.activeLanguage,
      subtitle: this.state.subtitles.activeLanguage,
    });
  }

  leadingZero(time: number) {
    return time.toString().length > 1 ? time.toString() : `0${time.toString()}`;
  }

  makeReadableTimestamp(time: number) {
    return `${this.leadingZero(Math.floor(time / 60))}:${this.leadingZero(Math.floor(time % 60))}`;
  }

  makeReadableTimestampLong(time: number, duration: number) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const totalMinutes = Math.floor(duration / 60);
    const totalSeconds = Math.floor(duration % 60);
    const minuteText = this.config.i18n ? this.config.i18n('film.scrubber_minutes') : 'Minutes';
    const secondText = this.config.i18n ? this.config.i18n('film.scrubber_seconds') : 'Seconds';
    const separator = this.config.i18n ? this.config.i18n('of') : 'of';
    return `${minutes} ${minuteText} ${seconds} ${secondText} ${separator} ${totalMinutes} ${minuteText} ${totalSeconds} ${secondText}`;
  }

  switchAudio(lang: string) {
    const wasPlaying = this.state.isPlaying;
    this.pause();
    if (this.config.onAudioLanguageChange) this.config.onAudioLanguageChange(lang);

    if (typeof this.audioHandler.audioTracks[lang] !== 'undefined') {
      this.state.audio.activeLanguage = lang;
      this.audioHandler.setActiveAudioTrack(lang);
      this.audioHandler.seek(this.video!.currentTime());
    }

    this.statistics.changeLanguage({
      audio: this.state.audio.activeLanguage,
      subtitle: this.state.subtitles.activeLanguage,
    });

    if (wasPlaying) {
      this.loadNewAudioInterval = setInterval(() => {
        if (this.audioHandler.audioTracks[lang] && this.audioHandler.audioTracks[lang].state() === 'loaded') {
          this.play();
          this.stopInterval('loadNewAudioInterval');
        }
      }, 25);
    }

    this.setPlaybackRate(this.state.playBackRate);
  }

  syncAudio() {
    this.audioHandler.seek(this.video!.currentTime());
  }

  syncVideo() {
    // Sets current video time to current audio time
    this.video!.currentTime(this.audioHandler.audioTracks[this.audioHandler.activeAudioTrack].seek());
  }

  initSeekBar() {
    const timestampElement = this.getElement('timestamp-hover')!;

    this.video!.on('timeupdate', () => {
      if (this.state.isSeeking) {
        return false;
      }

      const percentage = (this.video!.currentTime()! / this.video!.duration()!) * 100;
      this.controlButtons.scrubberProgress.setAttribute('style', `width: ${percentage}%`);
      this.controlButtons.timeStampCurrent.innerHTML = `${this.makeReadableTimestamp(this.video!.currentTime()!)}`;
      this.controlButtons.timeStampDuration.innerHTML = `${this.makeReadableTimestamp(this.video!.duration()!)}`;
      this.controlButtons.previewDuration.innerHTML = `${this.makeReadableTimestamp(this.video!.duration()!)}`;

      this.controlButtons.scrubberProgress.setAttribute('aria-valuenow', String(percentage));
      this.controlButtons.scrubberProgress.setAttribute('aria-valuemax', String(this.video!.duration()));
      this.controlButtons.scrubberProgress.setAttribute('aria-valuemin', String(0));
      this.controlButtons.scrubberProgress.setAttribute(
        'aria-valuetext',
        `${this.makeReadableTimestampLong(this.video!.currentTime()!, this.video!.duration()!)}`,
      );

      try {
        if (!window.Howler.noAudio) {
          // Detect diff between video and audio (unsynced)
          const diff = Math.abs(
            this.video!.currentTime()! - this.audioHandler.audioTracks[this.audioHandler.activeAudioTrack].seek(),
          );
          const audioDiff =
            this.audioHandler.audioTracks[this.audioHandler.activeAudioTrack].duration() *
            this.audioHandler.audioTracks[this.audioHandler.activeAudioTrack].seek();

          if (diff > 0.4 && audioDiff && this.state.isPlaying) {
            setTimeout(() => {
              this.syncVideo();
            }, 20); // Debug setTimeout
          }
        }
      } catch (error) {}
    });

    // When touching scrubber, add eventlisteners to videoWrapper to make seek work more smoothly
    this.controlButtons.scrubber.addEventListener(
      'touchstart',
      () => {
        this.state.isSeeking = true;

        const videoWrapper = document.getElementById(this.config.videoWrapID)!;

        videoWrapper.addEventListener('touchmove', this.state.startSeek as EventListener, {
          passive: true,
        });

        this.controlButtons.videoControls.style.pointerEvents = 'none';

        // Prevent buggy drag when seeking
        window.ontouchmove = function () {
          return false;
        };

        videoWrapper.addEventListener('touchend', this.state.stopSeek as EventListener);
      },
      { passive: true },
    );

    // Make timestamp follow touch on touchmove
    this.controlButtons.scrubberArea.addEventListener(
      'touchmove',
      (ev) => {
        const offsetX = ev.changedTouches[0].pageX;

        if (this.state.isSeeking) {
          return false;
        }

        const totalWidth = this.controlButtons.scrubber.offsetWidth;
        const percentage = offsetX / totalWidth;
        const vidTime = this.video!.duration()! * percentage;

        timestampElement.innerHTML = this.makeReadableTimestamp(vidTime);

        timestampElement.setAttribute('style', `opacity:1; visibility: visible; top: -30px; left: ${offsetX}px`);
      },
      { passive: true },
    );

    // Shrink scrubber and hide timestamp when touch ends
    this.controlButtons.scrubberArea.addEventListener('touchend', () => {
      this.controlButtons.scrubber.classList.remove('is-expanded');
      timestampElement.setAttribute('style', `opacity: 0; visibility: hidden`);
    });

    this.controlButtons.scrubber.addEventListener('mousedown', () => {
      this.state.isSeeking = true;

      window.addEventListener('mousemove', this.state.startSeek as EventListener);
      this.controlButtons.videoControls.style.pointerEvents = 'none';

      // Prevent buggy drag when seeking
      window.ondragstart = function () {
        return false;
      };

      window.addEventListener('mouseup', this.state.stopSeek as EventListener);
    });

    this.controlButtons.scrubberArea.addEventListener(
      'mousemove',
      (ev) => {
        if (this.state.isSeeking) {
          return false;
        }

        const scrubberRect = this.controlButtons.scrubber.getBoundingClientRect();
        const totalWidth = this.controlButtons.scrubber.offsetWidth;
        const percentage = (ev.clientX - scrubberRect.left) / totalWidth;
        const vidTime = this.video!.duration()! * percentage;
        if (vidTime <= 0 || percentage >= 1) {
          return; // Constrain timestamp bullet to valid values
        }
        const offsetLeft = ev.clientX - scrubberRect.left;

        timestampElement.innerHTML = this.makeReadableTimestamp(vidTime);

        timestampElement.setAttribute('style', `opacity:1; visibility: visible; top: -30px; left: ${offsetLeft}px`);
      },
      {
        passive: true,
      },
    );

    this.controlButtons.scrubberArea.addEventListener('mouseleave', () => {
      if (this.state.isSeeking) {
        return false;
      }
      this.controlButtons.scrubber.classList.remove('is-expanded');
      timestampElement.setAttribute('style', `opacity: 0; visibility: hidden`);
    });

    this.controlButtons.scrubberArea.addEventListener('mouseenter', () => {
      this.controlButtons.scrubber.classList.add('is-expanded');
    });
  }

  // Control seeking and timestamp position based on mouse or touch position
  startSeek(ev: MouseEvent | TouchEvent) {
    const scrubberCords = this.controlButtons.scrubber.getBoundingClientRect();
    let offsetX =
      ev.type === 'mousemove'
        ? (ev as MouseEvent).clientX - scrubberCords.x
        : (ev as TouchEvent).changedTouches[0].pageX - scrubberCords.left;
    const currentPosition = ev.type === 'mousemove' ? (ev as MouseEvent).x : (ev as TouchEvent).changedTouches[0].pageX;

    // If mouse or touch is dragged left of scrubber. Set offsetX to 0
    offsetX = currentPosition < scrubberCords.x ? 0 : offsetX;
    // If mouse or touch is dragged right of scrubber. Set offsetX to scrubber width
    offsetX = currentPosition > scrubberCords.right ? scrubberCords.width : offsetX;

    const timestampElement = this.getElement('timestamp-hover')!;

    const totalWidth = this.controlButtons.scrubber.offsetWidth;
    const percentage = offsetX / totalWidth;
    const vidTime = this.video!.duration()! * percentage;

    timestampElement.innerHTML = this.makeReadableTimestamp(vidTime);

    if (this.state.isSeeking) {
      this.controlButtons.scrubberProgress.setAttribute('style', `width: ${100 * percentage}%`);
      timestampElement.setAttribute('style', `opacity:1; visibility: visible; top: -30px; left: ${offsetX}px`);
    }
  }

  // When seeking stops. Place seek position on mouse or touch position and remove eventlisteners
  stopSeek(ev: MouseEvent | TouchEvent) {
    const scrubberCords = this.controlButtons.scrubber.getBoundingClientRect();
    let offsetX =
      ev.type === 'mouseup'
        ? (ev as MouseEvent).clientX - scrubberCords.x
        : (ev as TouchEvent).changedTouches[0].pageX - scrubberCords.left;
    const currentPosition = ev.type === 'mouseup' ? (ev as MouseEvent).x : (ev as TouchEvent).changedTouches[0].pageX;

    // If mouse or touch is dropped left of scrubber.
    offsetX = currentPosition < scrubberCords.x ? 0 : offsetX;
    // If mouse or touch is dropped right of scrubber
    offsetX = currentPosition > scrubberCords.right ? scrubberCords.width : offsetX;

    const totalWidth = this.controlButtons.scrubber.offsetWidth;
    const percentage = offsetX / totalWidth;
    const vidTime = this.video!.duration()! * percentage;

    if (this.state.isPlaying) {
      this.statistics.onStopPlaying(this.video!.currentTime()!, 'seek');
    }

    if (currentPosition > scrubberCords.right) {
      this.video!.currentTime(0);
      this.video!.pause();
    } else {
      this.video!.currentTime(vidTime);
      this.statistics.onStartPlaying(vidTime);
    }
    this.syncAudio();

    this.state.isSeeking = false;

    this.controlButtons.videoControls.style.removeProperty('pointer-events');

    document
      .getElementById(this.config.videoWrapID)!
      .removeEventListener('touchmove', this.state.startSeek as EventListener);
    document
      .getElementById(this.config.videoWrapID)!
      .removeEventListener('touchend', this.state.stopSeek as EventListener);
    window.removeEventListener('mousemove', this.state.startSeek as EventListener);
    window.removeEventListener('mouseup', this.state.stopSeek as EventListener);
  }

  areMenusOpen() {
    const menus = document.querySelectorAll('.js_video-controls-menu.is-active');
    return menus.length > 0;
  }

  closeAllMenus() {
    this.removeZIndexForContainer();
    const menus = document.querySelectorAll<HTMLElement>('.js_video-controls-menu.is-active');
    this.controlButtons.audioListWrapMobile.close();
    // forEach rather than for..of: some consumers compile this package without lib DOM.Iterable.
    // querySelectorAll returns a static list, so removing the class while iterating is safe.
    menus.forEach((menu) => {
      menu.classList.remove('is-active');
      this.toggleAccessibilityForDialog(menu, false);
    });
  }

  syncZIndexForContainer() {
    const fullscreenWrap = document.getElementById(this.config.fullscreenWrapID);
    if (!fullscreenWrap) {
      return;
    }
    const hasOpenMenu = fullscreenWrap.querySelector('.js_video-controls-menu.is-active') !== null;
    fullscreenWrap.classList.toggle('z-index-max', hasOpenMenu);
  }

  removeZIndexForContainer() {
    document.getElementById(this.config.fullscreenWrapID)?.classList.remove('z-index-max');
  }

  addExternalScript(url: string) {
    const script = document.createElement('script');
    script.setAttribute('src', url);
    document.head.appendChild(script);
  }

  toggleAccessibilityForDialog = (dialog: HTMLElement, open: boolean) => {
    if (open) {
      dialog.setAttribute('aria-hidden', 'false');
      dialog.removeAttribute('inert');
      // Shift focus to the dialog
      dialog.focus();
    } else {
      dialog.setAttribute('aria-hidden', 'true');
      dialog.setAttribute('inert', '');
    }
  };
}
